import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export enum SpendingGaugeLevel {
    WELL_BELOW = 'well_below',
    BELOW = 'below',
    SIMILAR = 'similar',
    ABOVE = 'above',
    WELL_ABOVE = 'well_above',
    NO_SPENDING = 'no_spending',
    ONLY_SPENDER = 'only_spender',
    NO_COMPARISON_DATA = 'no_comparison_data',
    ONLY_DATA_POINT = 'only_data_point',
}

export enum CompareFanSpendingResultCategoryConfidence {
    HIGH = 'high',
    MEDIUM = 'medium',
    LOW = 'low',
    NONE = 'none',
}

export interface CompareFanSpendingResultCategory {
    gauge: SpendingGaugeLevel;
    message: string;
    confidence: CompareFanSpendingResultCategoryConfidence;
    percentile?: number;
    hasSpending: boolean;
    comparedAgainst: number;
}

export interface BehavioralMetric {
    flag: string;
    confidence: CompareFanSpendingResultCategoryConfidence;
    message: string;
    severity: 'low' | 'medium' | 'high'; // Normalized severity level
}

export interface CompareFanSpendingResultCategory {
    gauge: SpendingGaugeLevel;
    message: string;
    confidence: CompareFanSpendingResultCategoryConfidence;
    percentile?: number;
    hasSpending: boolean;
    comparedAgainst: number;
}

export enum OverallSpendingPattern {
    HIGH_SPENDER = 'high_spender',
    LOW_SPENDER = 'low_spender',
    AVERAGE_SPENDER = 'average_spender',
    MIXED_SPENDER = 'mixed_spender',
    NO_PATTERN = 'no_pattern',
}

export interface OverallSpendingPatternAnalysis {
    pattern: OverallSpendingPattern;
    description: string;
    breakdown?: {
        above: number;
        similar: number;
        below: number;
        total: number;
    };
}

export interface CompareFanSpendingSummary {
    totalCreators: number;
    creatorsComparedAgainst: number;
    overallPattern: OverallSpendingPatternAnalysis;
    behavioralMetrics?: BehavioralMetric[];
}

export enum CompareFanSpendingResultStatus {
    SUCCESS = 'success',
    NO_DATA = 'no_data',
    NO_TARGET_DATA = 'no_target_data',
    ONLY_CREATOR = 'only_creator',
    ERROR = 'error',
}

export interface CompareFanSpendingResult {
    status: CompareFanSpendingResultStatus;
    message?: string;
    categories: {
        [category: string]: CompareFanSpendingResultCategory;
    } | null;
    summary?: CompareFanSpendingSummary;
}

interface SpendingStats {
    min: number;
    max: number;
    mean: number;
    median: number;
    q25: number;
    q75: number;
    count: number;
    sorted: number[];
}

export interface SimpleFanSpendingGaugeResult {
    status: CompareFanSpendingResultStatus;
    message?: string;
    categories?: { [key: string]: string };
    overallPattern?: OverallSpendingPattern;
}

const useInsightsUserSpendingsCompareDetailed = (onlyFansUserId: number | null | undefined) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    const fetchData = async (): Promise<CompareFanSpendingResult | null> => {
        const query: string = `${settingsContext.routes.sextforce.base}${subscriber._id}/onlyFansUsers/${onlyFansUserId}/insights/spendings/compare/detailed`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const query = useQuery(['insightsUserSpendingsCompareDetailed', onlyFansUserId], () => fetchData(), {
        refetchOnWindowFocus: false,
        refetchInterval: 1000 * 60 * 5, // 5 minutes
        enabled:
            userContext.jwtToken && settingsContext.apiKey && userContext.isAdmin && subscriber && subscriber._id && onlyFansUserId
                ? true
                : false,
    });

    return { ...query };
};

export default useInsightsUserSpendingsCompareDetailed;
